<script setup lang="ts">
import { type Datacenter, DatacenterStatus } from 'gpcore-grpc/gpcore/api/cloud/v1/region_pb';
import { computed, onMounted, watchEffect } from 'vue';
import OrderSection from '../OrderSection.vue';
import AsyncSectionContent from '../components/AsyncSectionContent.vue';
import SectionErrorMessage from '../components/SectionErrorMessage.vue';
import SectionLoadingMessage from '../components/SectionLoadingMessage.vue';
import DatacenterPickerItem from './DatacenterItem.vue';
import useDatacenterData from './composables/useDatacenterData';
import SectionMessage from '../components/SectionMessage.vue';

const datacenter = defineModel<Datacenter>();
const handleDatacenterSelect = (dc: Datacenter) => {
  if (dc.status === DatacenterStatus.AVAILABLE) {
    datacenter.value = dc;
  }
};

const {
  fetch,
  data: datacenters,
  displayData: sortedDatacenters,
  isLoading,
  latestLatency,
  error,
} = useDatacenterData();

const isAllDatacentersAvailable = computed(() => {
  return datacenters.value.every((dc) => dc.status === DatacenterStatus.AVAILABLE);
});

watchEffect(() => {
  if (datacenter.value == null && datacenters.value.length > 0) {
    const firstAvailableDatacenter = sortedDatacenters.value.find(
      (dc) => dc.status === DatacenterStatus.AVAILABLE,
    );
    datacenter.value = firstAvailableDatacenter;
  }
});

onMounted(() => {
  fetch();
});
</script>

<template>
  <OrderSection
    pre-title="Step 1"
    title="Select a region"
    :is-loading="isLoading"
    :is-done="datacenter != null"
    section-key="datacenter"
    v-playwright="'nodes-create-datacenter'"
  >
    <AsyncSectionContent
      :is-loading="isLoading"
      :error="error"
      :is-empty="datacenters.length === 0"
    >
      <template #loading>
        <SectionLoadingMessage> Loading datacenters... </SectionLoadingMessage>
      </template>

      <template #error>
        <SectionErrorMessage :error="error" v-playwright="'nodes-create-datacenter-error'">
          Error while loading datacenters
        </SectionErrorMessage>
      </template>

      <template #empty>
        <SectionMessage> No datacenters available. </SectionMessage>
      </template>

      <div class="tw-mt-0 tw-grid tw-grid-cols-1 tw-gap-4 md:tw-grid-cols-2 lg:tw-grid-cols-4">
        <DatacenterPickerItem
          v-for="(dc, index) in sortedDatacenters"
          :key="dc.id"
          class="tw-h-full"
          :datacenter="dc"
          :latency="latestLatency[dc.latencyEndpoint]"
          :is-all-datacenters-available="isAllDatacentersAvailable"
          :is-selected="datacenter != null && dc.id === datacenter.id"
          v-playwright="`nodes-create-datacenter-item-${index}`"
          @datacenter-selected="handleDatacenterSelect(dc)"
        />
      </div>
    </AsyncSectionContent>
  </OrderSection>
</template>
